/**
 * MobileMenu Component
 *
 * Slide-out mobile navigation drawer for screens < 768px
 * Features:
 * - Animated slide-in from right
 * - Backdrop with close on click
 * - Close on escape key
 * - All navigation links from desktop menu
 */

import { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Home,
  Brain,
  GraduationCap,
  BookOpen,
  HelpCircle,
  LogIn,
  Moon,
  Sun,
  FileText,
} from 'lucide-react';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useDarkMode } from '../hooks/useDarkMode';
import { AvatarDropdown } from './AvatarDropdown';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { isAuthenticated, user } = useAuthStore();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();
  const location = useLocation();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Check if a route is active
  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLinkClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const navItems = [
    { id: 'home', to: '/', label: 'Trang chủ', icon: Home },
    {
      id: 'cat-profile',
      to: '/ho-so-nang-luc',
      label: 'Hồ sơ năng lực',
      icon: FileText,
      isNew: true,
    },
    {
      id: 'assessment',
      to: '/huong-nghiep-cat?screen=instruction',
      label: 'Khám phá bản thân',
      icon: Brain,
    },
    { id: 'practice', to: '/thi-thu-thpt', label: 'Luyện đề THPT', icon: BookOpen },
    { id: 'majors', to: '/nganh-hoc', label: 'Ngành học', icon: GraduationCap },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]'
            onClick={onClose}
            aria-hidden='true'
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            style={{ right: 0 }}
            className='fixed top-0 h-full w-[280px] max-w-[85vw] bg-white dark:bg-[#1E293B] shadow-2xl z-[70] flex flex-col'
          >
            {/* Header */}
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700'>
              <span className='font-bold text-gray-900 dark:text-white'>Menu</span>
              <button
                onClick={onClose}
                className='p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-target'
                aria-label='Đóng menu'
              >
                <X className='w-5 h-5 text-gray-500 dark:text-gray-400' />
              </button>
            </div>

            {/* User Info (if authenticated) */}
            {isAuthenticated && user && (
              <div className='p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'>
                <div className='flex items-center gap-3'>
                  <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary'>
                    {getUserInitials()}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-semibold text-gray-900 dark:text-white truncate'>
                      {user.name ||
                        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                        'Người dùng'}
                    </p>
                    <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <nav className='flex-1 overflow-y-auto p-2'>
              <ul className='space-y-1'>
                {navItems.map(item => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.to.split('?')[0]);

                  return (
                    <li key={item.id}>
                      <Link
                        to={item.to}
                        onClick={handleLinkClick}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors touch-target ${
                          isActive
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className='w-5 h-5 flex-shrink-0' />
                        <span className='text-sm'>{item.label}</span>
                        {item.isNew && (
                          <span
                            className='inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold text-white rounded-full leading-none whitespace-nowrap ml-auto'
                            style={{
                              background: 'linear-gradient(135deg, #6366F1, #7C3AED, #EC4899)',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer-text 3s ease-in-out infinite',
                              boxShadow: '0 0 10px rgba(124, 58, 237, 0.3)',
                            }}
                          >
                            ✨ Mới
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Footer Actions */}
            <div className='p-4 border-t border-gray-200 dark:border-gray-700 space-y-2'>
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className='flex items-center gap-3 w-full px-4 py-3 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-target'
              >
                {isDark ? (
                  <>
                    <Sun className='w-5 h-5' />
                    <span className='text-sm'>Chế độ sáng</span>
                  </>
                ) : (
                  <>
                    <Moon className='w-5 h-5' />
                    <span className='text-sm'>Chế độ tối</span>
                  </>
                )}
              </button>

              {/* Login/Logout */}
              {!isAuthenticated && (
                <Link
                  to='/login'
                  onClick={handleLinkClick}
                  className='flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 transition-colors touch-target'
                >
                  <LogIn className='w-5 h-5' />
                  <span className='text-sm'>Đăng nhập</span>
                </Link>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
